// src/components/admin/AdminSystemLogsPage.js
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Container, Typography, Paper, Box, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, IconButton,
  TextField, InputAdornment, Chip, Button, Skeleton, Alert,
  MenuItem, FormControl, InputLabel, Select, Grid
} from '@mui/material';
import {
  Search, Refresh, Info, Warning, Error,
  CheckCircle, Schedule, ExpandMore, ExpandLess
} from '@mui/icons-material';
import { adminApi } from '../../api/adminApi';

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  in: { opacity: 1, y: 0 },
  out: { opacity: 0, y: -20 }
};

const pageTransition = {
  type: "tween",
  ease: "anticipate",
  duration: 0.6
};

const getLevelColor = (level) => {
  switch (level?.toLowerCase()) {
    case 'error':
      return 'error';
    case 'warning':
      return 'warning';
    case 'info':
      return 'info';
    case 'success':
      return 'success';
    default:
      return 'default';
  }
};

const getLevelIcon = (level) => {
  switch (level?.toLowerCase()) {
    case 'error':
      return <Error />;
    case 'warning':
      return <Warning />;
    case 'info':
      return <Info />;
    case 'success':
      return <CheckCircle />;
    default:
      return <Info />;
  }
};

export default function AdminSystemLogsPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [expandedRows, setExpandedRows] = useState(new Set());

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    setLoading(true);
    setError('');
    
    try {
      const logsData = await adminApi.getSystemLogs();
      setLogs(logsData.results || logsData);
    } catch (err) {
      console.error('Error fetching system logs:', err);
      setError(err.message || 'فشل في تحميل سجلات النظام');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleLevelFilterChange = (event) => {
    setLevelFilter(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowExpand = (logId) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(logId)) {
      newExpandedRows.delete(logId);
    } else {
      newExpandedRows.add(logId);
    }
    setExpandedRows(newExpandedRows);
  };

  // تصفية السجلات
  const filteredLogs = logs.filter(log => {
    const matchesSearch = 
      log.message?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.details?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.level?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesLevel = levelFilter === 'all' || log.level?.toLowerCase() === levelFilter;
    
    return matchesSearch && matchesLevel;
  });

  // حساب السجلات المعروضة
  const displayedLogs = filteredLogs.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('ar-SA');
  };

  const formatDetailsPreview = (details) => {
    if (!details) {
      return '-';
    }
    const singleLine = String(details).replace(/\s+/g, ' ').trim();
    if (!singleLine) {
      return '-';
    }
    return singleLine.length > 120 ? `${singleLine.slice(0, 120)}...` : singleLine;
  };

  const getLevelStats = () => {
    const stats = {
      error: logs.filter(log => log.level?.toLowerCase() === 'error').length,
      warning: logs.filter(log => log.level?.toLowerCase() === 'warning').length,
      info: logs.filter(log => log.level?.toLowerCase() === 'info').length,
      success: logs.filter(log => log.level?.toLowerCase() === 'success').length,
    };
    return stats;
  };

  const levelStats = getLevelStats();

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 2, mb: 2 }} />
          <Skeleton variant="text" width="40%" height={32} />
        </Box>
        <Paper sx={{ p: 3, borderRadius: 3 }}>
          <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
        </Paper>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Alert 
          severity="error" 
          sx={{ mb: 3, borderRadius: 2 }}
          action={
            <Button color="inherit" size="small" onClick={fetchLogs}>
              إعادة المحاولة
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <motion.div
      initial="initial"
      animate="in"
      exit="out"
      variants={pageVariants}
      transition={pageTransition}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h4" component="h1" fontWeight="700">
              سجلات النظام
            </Typography>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchLogs}
            >
              تحديث السجلات
            </Button>
          </Box>
          <Typography variant="body1" color="text.secondary">
            مراقبة أحداث النظام وسجلات الأخطاء
          </Typography>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'error.50' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Error color="error" />
                <Box>
                  <Typography variant="h6" fontWeight="600" color="error.main">
                    {levelStats.error}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    أخطاء
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'warning.50' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Warning color="warning" />
                <Box>
                  <Typography variant="h6" fontWeight="600" color="warning.main">
                    {levelStats.warning}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    تحذيرات
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'info.50' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Info color="info" />
                <Box>
                  <Typography variant="h6" fontWeight="600" color="info.main">
                    {levelStats.info}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    معلومات
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'success.50' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CheckCircle color="success" />
                <Box>
                  <Typography variant="h6" fontWeight="600" color="success.main">
                    {levelStats.success}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    نجاح
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Search and Filters */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="البحث في السجلات..."
                value={searchTerm}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>مستوى السجل</InputLabel>
                <Select
                  value={levelFilter}
                  label="مستوى السجل"
                  onChange={handleLevelFilterChange}
                >
                  <MenuItem value="all">جميع المستويات</MenuItem>
                  <MenuItem value="error">أخطاء</MenuItem>
                  <MenuItem value="warning">تحذيرات</MenuItem>
                  <MenuItem value="info">معلومات</MenuItem>
                  <MenuItem value="success">نجاح</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={`إجمالي السجلات: ${logs.length}`}
                  color="primary"
                  variant="outlined"
                />
                <Chip
                  label={`النتائج: ${filteredLogs.length}`}
                  color="info"
                  variant="outlined"
                />
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Logs Table */}
        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>المستوى</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>الرسالة</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>التاريخ</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>التفاصيل</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        {searchTerm || levelFilter !== 'all' ? 'لا توجد نتائج للبحث' : 'لا توجد سجلات'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  displayedLogs.map((log) => (
                    <React.Fragment key={log.id}>
                      <TableRow
                        sx={{
                          '&:hover': { bgcolor: 'grey.50' },
                          cursor: 'pointer'
                        }}
                        onClick={() => handleRowExpand(log.id)}
                      >
                        <TableCell>
                          <Chip
                            label={log.level}
                            color={getLevelColor(log.level)}
                            icon={getLevelIcon(log.level)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="500">
                            {log.message}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Schedule fontSize="small" color="action" />
                            <Typography variant="body2">
                              {formatDate(log.timestamp)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                            <Typography
                              variant="body2"
                              color={log.details ? 'text.primary' : 'text.disabled'}
                              sx={{
                                maxWidth: 320,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {formatDetailsPreview(log.details)}
                            </Typography>
                            <IconButton size="small">
                              {expandedRows.has(log.id) ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                      {expandedRows.has(log.id) && log.details && (
                        <TableRow>
                          <TableCell colSpan={4} sx={{ bgcolor: 'grey.50', py: 2 }}>
                            <Paper sx={{ p: 2, bgcolor: 'white' }}>
                              <Typography variant="body2" component="pre" sx={{ 
                                whiteSpace: 'pre-wrap', 
                                fontFamily: 'inherit',
                                fontSize: '0.875rem'
                              }}>
                                {log.details}
                              </Typography>
                            </Paper>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={filteredLogs.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="صفوف في الصفحة:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} من ${count}`}
          />
        </Paper>

        {/* Summary */}
        <Paper sx={{ p: 3, mt: 4, borderRadius: 3, bgcolor: 'grey.50' }}>
          <Typography variant="h6" fontWeight="600" gutterBottom>
            ملخص السجلات
          </Typography>
          <Typography variant="body2" color="text.secondary">
            يتم تحديث السجلات تلقائياً كل دقيقة. يمكنك استخدام البحث والتصفية للعثور على السجلات المطلوبة.
          </Typography>
        </Paper>
      </Container>
    </motion.div>
  );
}
